/**
 * AuiDelegatedTaskCard
 *
 * AUI surface component for tracking a delegated task in real-time.
 * Renders a fixed-height card with a rolling CoT timeline (last 3 entries)
 * and collapses to a result summary on completion.
 *
 * The operator agent includes this component in its task-dashboard surface:
 *   { type: "DelegatedTaskCard", props: { taskId, delegatedRunId, agentName, ... } }
 *
 * The component subscribes to WebSocket events for the delegated run,
 * showing live progress independently of surface updates.
 */

import { memo, useContext, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence } from 'motion/react';
import {
  Bot,
  Loader2,
  X,
  Clock,
  AlertCircle,
  Ban,
  EllipsisVertical,
  ExternalLink,
  Sparkles,
  Copy,
  RefreshCw,
} from 'lucide-react';
import { TimelineEntryRow } from '../../assistant-ui/cot-timeline';
import Badge, { getStatusVariant } from '../../ui/Badge';
import { AnimatedListItem } from '../../ui/AnimatedListItem';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '../../ui/dropdown-menu';
import { Tooltip, TooltipTrigger, TooltipContent } from '../../ui/tooltip';
import { useDelegatedTaskStatus, delegatedTaskStatusRegistry, type InitialTaskSummary } from '../../../hooks/useDelegatedTaskStatus';
import { useCanvases } from '../../../contexts/CanvasContext';
import { TaskDashboardContext } from './TaskDashboardContext';
import { useToast } from '../../ui/Toast';
import { spaceApi } from '../../../services/api';
import { useAssistantThreadActions } from '../../../contexts/AssistantThreadActionsContext';
import { useAuth } from '../../../store/auth-context';
import { routes } from '@/lib/routes';
import { MarkdownContent } from '../../ui/markdown-content';
import { formatElapsed, formatTokenCount } from '../../../utils/format';
import { formatRelativeTime } from '@/pages/activity/utils';
import type { AuiRendererProps } from '../registry';
import type { DelegatedTaskStatus } from '../../../types/delegated-tasks';
import type { ResolvedSurfaceRef } from '../../../types';
import { cleanDisplayText } from '@/utils/display-text';

// ─── Status styling ────────────────────────────────────────────────────────

const STATUS_BORDER: Record<DelegatedTaskStatus, string> = {
  pending: 'border-l-muted-foreground/40',
  running: 'border-l-accent-run-border',
  completed: 'border-l-status-success-border',
  failed: 'border-l-status-error-border',
  cancelled: 'border-l-status-warning-border',
};

/** Number of CoT entries to show in the rolling window */
const MAX_VISIBLE_ENTRIES = 3;

// ─── Main component ────────────────────────────────────────────────────────

export const AuiDelegatedTaskCard = memo(function AuiDelegatedTaskCard({ node, onAction }: AuiRendererProps) {
  const props = node.component.props ?? {};
  const taskId = props.taskId as string | undefined;
  const delegatedRunId = props.delegatedRunId as string | undefined;
  const { user } = useAuth();
  const runSpaceId =
    (props.spaceId as string | undefined) ??
    user?.preferences?.lastSpaceId ??
    user?.defaultSpaceId ??
    '';
  const agentName = (props.agentName as string) ?? 'Agent';
  const title = (props.title as string) ?? '';
  const surfaceStatus = (props.status as DelegatedTaskStatus) ?? 'pending';
  const canvasId = props.canvasId as string | undefined;
  const referencedSurfaces = (props.referencedSurfaces as ResolvedSurfaceRef[] | undefined) ?? [];
  const isInPlace = referencedSurfaces.length > 0;
  const actions = node.component.actions;

  const updatedAt = props.updatedAt as string | undefined;

  // Build initial summary from props to avoid individual getRunStatus() calls
  const initialSummary = useMemo((): InitialTaskSummary | undefined => {
    const isTerminal = surfaceStatus === 'completed' || surfaceStatus === 'failed' || surfaceStatus === 'cancelled';
    if (!isTerminal) return undefined;
    return {
      status: surfaceStatus,
      durationMs: props.durationMs as number | undefined,
      toolCallCount: props.toolCallCount as number | undefined,
      usage: props.usage as InitialTaskSummary['usage'],
      responsePreview: props.responsePreview as string | undefined,
      error: props.error as string | undefined,
      updatedAt,
    };
  }, [surfaceStatus, props.durationMs, props.toolCallCount, props.usage, props.responsePreview, props.error, updatedAt]);

  // Real-time status from WebSocket events + props summary + REST fallback
  const live = useDelegatedTaskStatus(delegatedRunId, initialSummary);

  // Don't fall back to surfaceStatus until the hook has resolved (REST or WS).
  // This avoids the pending→completed flicker on remount.
  const effectiveStatus: DelegatedTaskStatus | null = live.status ?? (live.resolved ? surfaceStatus : null);
  const displayStatus: DelegatedTaskStatus = effectiveStatus ?? 'pending';
  const isActive = displayStatus === 'running' || displayStatus === 'pending';
  const isTerminal = displayStatus === 'completed' || displayStatus === 'failed' || displayStatus === 'cancelled';
  const isLoading = effectiveStatus === null;

  // Register live status for Grid sorting
  useEffect(() => {
    if (delegatedRunId && effectiveStatus) {
      delegatedTaskStatusRegistry.set(delegatedRunId, effectiveStatus);
    }
    return () => { if (delegatedRunId) delegatedTaskStatusRegistry.delete(delegatedRunId); };
  }, [delegatedRunId, effectiveStatus]);

  // Elapsed display
  const elapsedSec = live.elapsedMs != null
    ? Math.round(live.elapsedMs / 1000)
    : null;

  // Rolling CoT entries
  const visibleEntries = useMemo(
    () => live.timelineEntries.slice(-MAX_VISIBLE_ENTRIES),
    [live.timelineEntries],
  );

  const { surfaces, canvases, setActiveCanvasId, loadCanvasSurfaces, removeSurfaceNode, requestScrollToSurface, conversationId } = useCanvases();
  const toast = useToast();

  // Find which surface contains this card node (needed for the surface-path
  // delete; null when the card is rendered by AuiTaskDashboard via a
  // synthetic node id)
  const hostSurface = useMemo(() => {
    for (const s of surfaces.values()) {
      if (s.spec?.updates?.some((n) => n.id === node.id)) return s;
    }
    return null;
  }, [surfaces, node.id]);

  // Dashboard removal callback — present when this card is rendered inside
  // AuiTaskDashboard, absent when it's rendered as a real surface node.
  const dashboardCtx = useContext(TaskDashboardContext);

  // Cancel action
  const handleCancel = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    const cancelAction = actions?.find((a) => a.name === 'cancel_task');
    if (cancelAction && onAction) {
      await onAction(cancelAction);
    }
  }, [actions, onAction]);

  // View results — switch to task canvas and ensure surfaces are loaded
  const handleViewResults = useCallback(() => {
    if (canvasId) {
      loadCanvasSurfaces(canvasId);
      setActiveCanvasId(canvasId);
    }
  }, [canvasId, setActiveCanvasId, loadCanvasSurfaces]);

  // Delete task — drop the card locally and fire the DELETE; chooses between
  // the dashboard path (synthetic node, not in any surface spec) and the
  // surface path (real node in a surface's updates).
  const handleDelete = useCallback(async () => {
    if (dashboardCtx && taskId) {
      dashboardCtx.removeTask(taskId);
    } else if (hostSurface) {
      removeSurfaceNode(hostSurface.canvasId, hostSurface.surfaceId, node.id);
    } else {
      return;
    }
    // Dismiss via dedicated API route (fire-and-forget)
    if (conversationId && taskId) {
      if (runSpaceId) {
        spaceApi(runSpaceId).delegatedTasks.dismiss(conversationId, taskId).catch(() => {});
      }
    }
    // Also dispatch to agent if an action is defined
    const deleteAction = actions?.find((a) => a.name === 'delete_task');
    if (deleteAction && onAction) {
      onAction(deleteAction).catch(() => {});
    }
  }, [dashboardCtx, hostSurface, removeSurfaceNode, node.id, conversationId, taskId, actions, onAction]);

  // Run again — rerun the delegated task via API
  const handleRunAgain = useCallback(async () => {
    if (!conversationId || !taskId) return;
    try {
      if (!runSpaceId) throw new Error('spaceId required to rerun task');
      await spaceApi(runSpaceId).delegatedTasks.rerun(conversationId, taskId);
      toast.success('Task re-submitted');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to re-run task');
    }
    // Also dispatch to agent if an action is defined
    const runAgainAction = actions?.find((a) => a.name === 'run_again' || a.name === 'clone_task');
    if (runAgainAction && onAction) {
      onAction(runAgainAction).catch(() => {});
    }
  }, [conversationId, taskId, actions, onAction, toast]);

  // Run details — open the standalone runs page for this delegated run
  const navigate = useNavigate();
  const handleRunDetails = useCallback(() => {
    if (!delegatedRunId) return;
    navigate(routes.console.run(delegatedRunId));
  }, [navigate, delegatedRunId]);

  const handleCopyRunId = useCallback(async () => {
    if (!delegatedRunId) return;
    try {
      await navigator.clipboard.writeText(delegatedRunId);
      toast.success('Run id copied');
    } catch {
      toast.error('Failed to copy run id');
    }
  }, [delegatedRunId, toast]);

  // Analyze run — ask the operator agent (via a user message) to delegate
  // run-analysis to the architect. Routed through the host page's runtime so
  // the user message renders immediately in the thread and the run streams
  // through the normal adapter path (active runId, surface events, etc.).
  const threadActions = useAssistantThreadActions();
  const handleAnalyzeRun = useCallback(() => {
    if (!delegatedRunId) return;
    if (!threadActions) {
      toast.error('Cannot submit analysis from this view');
      return;
    }
    const subject = title ? ` (${agentName} — ${title})` : ` (${agentName})`;
    // Embed the run id inside a markdown link so the user sees a readable
    // "this run" link instead of a raw id. The backend agent still sees the
    // id in the link target and can parse it out.
    const runLink = `[this run](${routes.console.run(delegatedRunId)})`;
    const message =
      `Please ask the architect agent to analyze ${runLink}${subject}. ` +
      `Focus on duration, tool-call efficiency, and any anomalies, and surface the analysis as a canvas artifact.`;
    threadActions.submitUserMessage(message);
  }, [delegatedRunId, threadActions, agentName, title, toast]);

  const borderClass = isLoading
    ? 'border-l-muted-foreground/20'
    : STATUS_BORDER[displayStatus] ?? STATUS_BORDER.pending;

  return (
    <div
      data-testid="aui-delegated-task-card"
      className={`group/task-card min-w-0 min-h-[180px] rounded-lg border border-l-2 ${borderClass} bg-card transition-colors ${
        displayStatus === 'running' && !isLoading ? 'animate-[task-pulse_2s_ease-in-out_infinite]' : ''
      }`}
    >
      <div className="p-3">
        {/* Header row: agent name + status + date + menu */}
        <div className="flex items-center gap-2 mb-1">
          <Bot className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="text-sm text-muted-foreground truncate">
            {agentName}
          </span>
          {isLoading ? (
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground/50">
              <Loader2 className="h-3 w-3 animate-spin" />
            </span>
          ) : (
            <Badge variant={getStatusVariant(displayStatus)}>
              {displayStatus}
            </Badge>
          )}

          {/* Spacer */}
          <div className="flex-1" />

          {/* Last updated timestamp — prefer API's updatedAt for terminal tasks,
              use live WS timestamp for running tasks */}
          {(() => {
            const displayTs = isTerminal && updatedAt
              ? updatedAt
              : live.lastEventAt
                ? new Date(live.lastEventAt).toISOString()
                : null;
            if (!displayTs) return null;
            return (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-xs text-muted-foreground cursor-default shrink-0">
                    {formatRelativeTime(displayTs)}
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top">
                  {new Date(displayTs).toLocaleString()}
                </TooltipContent>
              </Tooltip>
            );
          })()}

          <div className="flex items-center gap-1">
            {/* Quick dismiss — reveals on card hover. Same action as the menu's
                "Delete" item; gives users a one-click escape hatch. */}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); void handleDelete(); }}
              aria-label="Dismiss"
              title="Dismiss"
              className="focus-ring rounded p-0.5 text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground group-hover/task-card:opacity-100 focus-visible:opacity-100 shrink-0"
            >
              <X className="h-3.5 w-3.5" />
            </button>

            {/* 3-dot menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="focus-ring rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors shrink-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  <EllipsisVertical className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" sideOffset={4}>
                <DropdownMenuItem onClick={handleRunAgain}>
                  <RefreshCw className="mr-2 h-3.5 w-3.5" />
                  Run again
                </DropdownMenuItem>
                {delegatedRunId && (
                  <DropdownMenuItem onClick={handleRunDetails}>
                    <ExternalLink className="mr-2 h-3.5 w-3.5" />
                    Run details
                  </DropdownMenuItem>
                )}
                {delegatedRunId && (
                  <DropdownMenuItem onClick={handleAnalyzeRun}>
                    <Sparkles className="mr-2 h-3.5 w-3.5" />
                    Analyze run
                  </DropdownMenuItem>
                )}
                {delegatedRunId && (
                  <DropdownMenuItem onClick={handleCopyRunId}>
                    <Copy className="mr-2 h-3.5 w-3.5" />
                    Copy run id
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleDelete}>
                  <X className="mr-2 h-3.5 w-3.5" />
                  Dismiss
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Title / instruction — for single-ref in-place updates, prefix with
            "Updating"/"Updated" so users see at a glance which surface is being
            mutated. Multi-ref keeps the original title and uses the footer
            chips for navigation. */}
        {(() => {
          if (isInPlace && referencedSurfaces.length === 1) {
            const ref = referencedSurfaces[0];
            const refLabel = cleanDisplayText(ref.title) || ref.surfaceName;
            const prefix = isActive ? 'Updating' : displayStatus === 'completed' ? 'Updated' : null;
            const headline = prefix ? `${prefix} ${refLabel}` : (cleanDisplayText(title) || refLabel);
            return (
              <p className="text-sm font-medium text-foreground line-clamp-1 mb-1">
                {headline}
              </p>
            );
          }
          return title ? (
            <p className="text-sm font-medium text-foreground line-clamp-1 mb-1">
              {cleanDisplayText(title)}
            </p>
          ) : null;
        })()}

        {/* Fixed-height body area — always reserves space for 3 CoT lines */}
        <div className="h-[5.5rem] overflow-hidden mt-1">
          {isLoading && (
            <div className="flex flex-col gap-1.5 pt-1 animate-pulse">
              <div className="h-3 w-3/4 rounded bg-muted-foreground/10" />
              <div className="h-3 w-1/2 rounded bg-muted-foreground/10" />
            </div>
          )}

          {!isLoading && displayStatus === 'pending' && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground/60 pt-1">
              <Clock className="h-3 w-3 shrink-0" />
              <span>Waiting to start...</span>
            </div>
          )}

          {!isLoading && displayStatus === 'running' && (
            <div className="flex flex-col justify-end h-full">
              <AnimatePresence initial={false}>
                {visibleEntries.map((entry) => (
                  <AnimatedListItem key={entry.id}>
                    <div className="py-0.5">
                      <TimelineEntryRow entry={entry} />
                    </div>
                  </AnimatedListItem>
                ))}
              </AnimatePresence>
            </div>
          )}

          {!isLoading && displayStatus === 'completed' && live.resultSummary && (
            <div className="text-xs text-muted-foreground pt-1">
              <div className="line-clamp-4 text-foreground/70 text-[11px] leading-snug">
                <MarkdownContent size="xs">{live.resultSummary}</MarkdownContent>
              </div>
            </div>
          )}

          {!isLoading && displayStatus === 'failed' && (
            <div className="text-xs pt-1">
              <div className="flex items-center gap-1.5 text-status-error-fg">
                <AlertCircle className="h-3 w-3 shrink-0" />
                <span>Failed</span>
              </div>
              {live.resultSummary && (
                <div className="line-clamp-3 text-muted-foreground mt-1 text-[11px] leading-snug">
                  <MarkdownContent size="xs">{live.resultSummary}</MarkdownContent>
                </div>
              )}
            </div>
          )}

          {!isLoading && displayStatus === 'cancelled' && (
            <div className="flex items-center gap-1.5 text-xs text-status-warning-fg pt-1">
              <Ban className="h-3 w-3 shrink-0" />
              <span>Cancelled</span>
            </div>
          )}
        </div>

        {/* Footer: stats (left) + actions (right) */}
        <div className="flex items-center gap-2 mt-2">
          {/* Stats */}
          <div className="flex items-center gap-2 text-xs tabular-nums text-muted-foreground">
            {elapsedSec != null && (
              <span className="flex items-center gap-1 shrink-0">
                <Clock className="h-3 w-3" />
                {formatElapsed(elapsedSec)}
              </span>
            )}
            {live.toolCallCount > 0 && (
              <span className="shrink-0">
                {live.toolCallCount} tool{live.toolCallCount !== 1 ? 's' : ''}
              </span>
            )}
            {live.usage && (
              <span className="shrink-0">
                {formatTokenCount(live.usage.inputTokens)} in / {formatTokenCount(live.usage.outputTokens)} out
              </span>
            )}
          </div>

          {/* Actions */}
          <div className="ml-auto flex flex-wrap items-center gap-1">
            {!isLoading && displayStatus === 'completed' && (
              isInPlace ? (
                referencedSurfaces.map((ref) => (
                  <button
                    key={ref.surfaceId}
                    type="button"
                    className="focus-ring inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 px-2 py-0.5 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    onClick={() => requestScrollToSurface(ref.canvasId, ref.surfaceId)}
                  >
                    <RefreshCw className="h-3 w-3" />
                    <span className="max-w-[10rem] truncate">{cleanDisplayText(ref.title) || ref.surfaceName}</span>
                  </button>
                ))
              ) : (
                canvasId && canvases.has(canvasId) && (
                  <button
                    type="button"
                    className="focus-ring rounded px-2 py-0.5 text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                    onClick={handleViewResults}
                  >
                    View Results
                  </button>
                )
              )
            )}
            {isActive && actions?.find((a) => a.name === 'cancel_task') && (
              <button
                type="button"
                className="focus-ring rounded p-1 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                onClick={handleCancel}
                title="Cancel task"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}, (prev, next) => {
  const pp = prev.node.component.props ?? {};
  const np = next.node.component.props ?? {};
  const prefs = (pp.referencedSurfaces as ResolvedSurfaceRef[] | undefined) ?? [];
  const nrefs = (np.referencedSurfaces as ResolvedSurfaceRef[] | undefined) ?? [];
  const refsEqual =
    prefs.length === nrefs.length &&
    prefs.every((r, i) => r.surfaceId === nrefs[i]?.surfaceId);
  return pp.taskId === np.taskId
    && pp.delegatedRunId === np.delegatedRunId
    && pp.status === np.status
    && pp.agentName === np.agentName
    && pp.title === np.title
    && pp.canvasId === np.canvasId
    && pp.durationMs === np.durationMs
    && pp.toolCallCount === np.toolCallCount
    && pp.responsePreview === np.responsePreview
    && pp.error === np.error
    && refsEqual
    && prev.onAction === next.onAction;
});

// (Timeline entry row moved to ../../assistant-ui/cot-timeline.tsx so the
//  conversation thread CoT and the task card share the same renderer.)
